import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { SystemUserModel } from "../../models/api/SystemUserModel";
import { SystemUserFindResultModel } from "../../models/api/SystemUserFindResultModel";

export abstract class SystemUserBaseService extends BaseService {

    private urlPart: string = 'SystemUser';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of SystemUser records from the database
    /// /SystemUser
    /// </summary>
    public findSystemUser(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = ""): Observable<SystemUserFindResultModel>
    {
        // Find SystemUser Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}`;

        if (!environment.production)
            console.log("Find SystemUser: Calling " + url);

        return this.http.get<SystemUserFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: SystemUserFindResultModel = Object.assign(new SystemUserFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single SystemUser record from the database
    /// /SystemUser/{systemuserid}
    /// </summary>
    /// <param name="systemuserid"></param>
    /// <returns></returns>
    public getSystemUser(systemuserid: number): Observable<SystemUserModel>
    {

        // Get Single SystemUser Record

        // TODO: Get Single SystemUser Record

        const url = `${super.getBaseUrl()}/${systemuserid}`;

        if (!environment.production)
            console.log("Get SystemUser: Calling " + url);

        return this.http.get<SystemUserModel>(url)
            .pipe(
                map(next => {
                    let o: SystemUserModel = Object.assign(new SystemUserModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// POST: Adds a new SystemUser record to the database
    /// /SystemUser/{systemuserid}
    /// </summary>
    /// <param name="systemuserid"></param>
    /// <returns></returns>
    public createSystemUser(newRecord: SystemUserModel): Observable<SystemUserModel>
    {
        // Add Single SystemUser Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create SystemUser:Calling " + url);

        return this.http.post<SystemUserModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: SystemUserModel = Object.assign(new SystemUserModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing SystemUser record in the database
    /// /SystemUser/{systemuserid}
    /// </summary>
    /// <param name="systemuserid"></param>
    /// <returns></returns>
    public updateSystemUser(systemuserid: number, updatedRecord: SystemUserModel): Observable<SystemUserModel>
    {
        // Update Single SystemUser Record

        const url = `${super.getBaseUrl()}/${systemuserid}`;

        if (!environment.production)
            console.log("Update SystemUser: Calling " + url);

        return this.http.put<SystemUserModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: SystemUserModel = Object.assign(new SystemUserModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateSystemUserRecord(updatedRecord: SystemUserModel): Observable<SystemUserModel>
    {
        return this.updateSystemUser(updatedRecord.systemUserID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing SystemUser record from the database
    /// /SystemUser/{systemuserid}
    /// </summary>
    /// <param name="systemuserid"></param>
    /// <returns></returns>
    public deleteSystemUser(systemuserid: number): Observable<ApiResult>
    {
        // Delete Single SystemUser Record

        const url = `${super.getBaseUrl()}/${systemuserid}`;

        if (!environment.production)
            console.log("Delete SystemUser: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteSystemUserRecord(deleteRecord: SystemUserModel): Observable<ApiResult>
    {
        // Delete Single SystemUser Record
        return this.deleteSystemUser(deleteRecord.systemUserID);
    }

}
